import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import { Box, FormControl, MenuItem, Select, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';
import {
  ModalContainer,
  ModalHeader,
  ModalCloseIcon,
  ContentArea,
  ContentInner,
  SectionCard,
  SectionHeader,
  FormLabel,
  RequiredMark,
  TimeRow,
  TimeColon,
  TimeTilde,
  TwoColumnRow,
  StyledTextarea,
  FooterBar,
  FooterRightGroup,
} from '../../../lesson/lessonModal/PrivateLessonEditModal/PrivateLessonEditModalStyled';
import CommonModalTitle from '../../../../form-components/CommonModalTitle';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonDatePicker from '../../../../form-components/CommonDatePicker';
import { getAllStaffs } from '../../../../api/staff';
import { createConsultation, updateConsultation } from '../../../../api/consultation';
import { withOverlapConfirm } from '../../../../utils/overlapConfirm';
import { VISIT_PATH_ETC_MAX_LENGTH, VISIT_PATH_OPTIONS, VISIT_PATH_OTHER } from '../../../../constants/visitPath';
import useConsultOptions from '../../../../hooks/useConsultOptions';
import {
  CONSULT_METHOD_OPTIONS,
  CONSULT_PURPOSE_OPTIONS,
  CONSULT_RESULT_OPTIONS,
} from '../../../../constants/consultation';


const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => i * 5);

const parseTime = timeStr => {
  if (!timeStr) return [0, 0];
  const [h, m] = timeStr.split(':');
  return [parseInt(h, 10) || 0, parseInt(m, 10) || 0];
};

const initialFormData = {
  title: '',
  consultDate: dayjs(),
  managerId: '',
  startHour: 9,
  startMinute: 0,
  endHour: 9,
  endMinute: 50,
  consultMethod: '',
  consultPurpose: '',
  consultResult: 'SCHEDULED',
  visitPath: '',
  visitPathEtc: '',
  exercisePurposes: [],
  content: '',
};

// mode: 'create' | 'edit' | 'detail'
const ConsultationEdit = ({ open, onClose, editData, mode = 'create', onSuccess, onEdit, memberId }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const token = user?.token;
  const headBranchId = user?.headBranch?.headBranchId;
  const branchId = user?.branch?.branchId;
  const queryClient = useQueryClient();
  const toast = useToast();

  const isDetailMode = mode === 'detail';
  const isEditMode = mode === 'edit';

  const [formData, setFormData] = useState(initialFormData);

  const { data: staffList } = useQuery({
    queryKey: ['ConsultationStaffList', headBranchId, branchId],
    queryFn: () => {
      const query = QueryString.stringify({ headBranchId, branchId, page: 1, limit: 100 });
      return getAllStaffs(token, query);
    },
    enabled: !!token && !!headBranchId,
  });

  useEffect(() => {
    if (!open) return;
    if ((isEditMode || isDetailMode) && editData) {
      const [startHour, startMinute] = parseTime(editData.consultStartTime);
      const [endHour, endMinute] = parseTime(editData.consultEndTime);
      setFormData({
        title: editData.title || '',
        consultDate: editData.consultDate ? dayjs(editData.consultDate) : dayjs(),
        managerId: editData.managerId || '',
        startHour,
        startMinute,
        endHour,
        endMinute,
        consultMethod: editData.consultMethod || '',
        consultPurpose: editData.consultPurpose || '',
        consultResult: editData.consultResult || 'SCHEDULED',
        visitPath: editData.visitPath || '',
        visitPathEtc: editData.visitPathEtc || '',
        exercisePurposes: editData.exercisePurposes || [],
        content: editData.content || '',
      });
    } else {
      setFormData(initialFormData);
    }
  }, [open, isEditMode, isDetailMode, editData]);

  const { exercisePurposes: exercisePurposeOptions } = useConsultOptions();

  const togglePurpose = value =>
    setFormData(prev => ({
      ...prev,
      exercisePurposes: prev.exercisePurposes.includes(value)
        ? prev.exercisePurposes.filter(v => v !== value)
        : [...prev.exercisePurposes, value],
    }));

  const handleChange = (field, value) =>
    setFormData(prev => {
      // 시작 시간 > 종료 시간이면 종료 시간(시 단위)도 함께 끌어올림. 분은 유지.
      if (field === 'startHour' && value > prev.endHour) {
        return { ...prev, startHour: value, endHour: value };
      }
      return { ...prev, [field]: value };
    });

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  const createMutation = useMutation({
    mutationFn: submitData => withOverlapConfirm(force => createConsultation(submitData, token, force)),
    onSuccess: () => {
      toast.success(t('상담이 등록되었습니다.'));
      handleClose();
      // 목록 키는 ['consultationList', scope, targetId] 라 memberId 를 2번째에 넣으면 아무것도 안 걸린다.
      queryClient.invalidateQueries({ queryKey: ['consultationList'] });
    },
    onError: error => {
      // 겹침 확인창에서 취소했으면 안내 없이 모달을 열어 둔다
      if (error?.silent) return;
      toast.error(t('상담 등록에 실패했습니다.'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: submitData =>
      withOverlapConfirm(force =>
        updateConsultation(editData?.consultationId || editData?.id, submitData, token, force),
      ),
    onSuccess: () => {
      toast.success(t('상담이 수정되었습니다.'));
      handleClose();
      queryClient.invalidateQueries({ queryKey: ['consultationList'] });
    },
    onError: error => {
      if (error?.silent) return;
      toast.error(t('상담 수정에 실패했습니다.'));
    },
  });

  const handleSubmit = () => {
    // 종료 시간은 시작 시간보다 뒤여야 함 (같거나 앞이면 차단)
    const startTotal = formData.startHour * 60 + formData.startMinute;
    const endTotal = formData.endHour * 60 + formData.endMinute;
    if (startTotal >= endTotal) {
      toast.error(t('상담 종료 시간은 시작 시간보다 늦어야 합니다.'));
      return;
    }

    // 서버가 요구하는 값을 안 채우면 400 이 나는데 실패 안내가 없어 저장된 것처럼 보였다.
    if (!formData.title.trim()) {
      toast.error(t('상담 제목을 입력해 주세요.'));
      return;
    }

    // 퇴사 등으로 담당자가 직원 목록에 없을 수 있다. 그대로 두고 내용만 고치는 경우까지 막지 않는다.
    const selectedManager = staffList?.content?.find(staff => staff.id === formData.managerId);
    const managerName =
      selectedManager?.name ||
      (formData.managerId === editData?.managerId ? editData?.managerName : '');
    if (!formData.managerId || !managerName) {
      toast.error(t('담당자를 선택해 주세요.'));
      return;
    }
    const pad = n => String(n).padStart(2, '0');

    const submitData = {
      consultType: 'MEMBER',
      memberId,
      branchId,
      title: formData.title,
      consultDate: formData.consultDate ? dayjs(formData.consultDate).format('YYYY-MM-DD') : null,
      consultStartTime: `${pad(formData.startHour)}:${pad(formData.startMinute)}`,
      consultEndTime: `${pad(formData.endHour)}:${pad(formData.endMinute)}`,
      consultMethod: formData.consultMethod || null,
      consultPurpose: formData.consultPurpose || null,
      consultResult: formData.consultResult || 'SCHEDULED',
      visitPath: formData.visitPath || null,
      visitPathEtc: formData.visitPath === VISIT_PATH_OTHER ? formData.visitPathEtc || null : null,
      exercisePurposes: formData.exercisePurposes,
      managerId: Number(formData.managerId),
      managerName,
      content: formData.content,
    };

    if (isEditMode) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
    onSuccess?.(submitData);
  };

  const inputSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': { borderRadius: '4px' },
    '& .MuiOutlinedInput-input': { fontSize: '13px' },
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        <ModalHeader>
          <CommonModalTitle>
            {isDetailMode ? t('상담 상세') : isEditMode ? t('상담 수정') : t('상담 등록')}
          </CommonModalTitle>
          <ModalCloseIcon onClick={handleClose} />
        </ModalHeader>

        <ContentArea>
          <ContentInner>
            {/* 기본 정보 */}
            <SectionCard>
              <SectionHeader variant="label">{t('기본 정보')}</SectionHeader>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                <FormLabel>
                  {t('상담 제목')}
                  <RequiredMark>*</RequiredMark>
                </FormLabel>
                <TextField
                  size="small"
                  sx={inputSx}
                  placeholder={t('상담 제목을 입력해 주세요')}
                  value={formData.title}
                  onChange={e => handleChange('title', e.target.value)}
                  disabled={isDetailMode}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormLabel>
                  {t('담당자')}
                  <RequiredMark>*</RequiredMark>
                </FormLabel>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    displayEmpty
                    value={formData.managerId}
                    onChange={e => handleChange('managerId', e.target.value)}
                    disabled={isDetailMode}>
                    <MenuItem value="" disabled>
                      {t('선택')}
                    </MenuItem>
                    {staffList?.content?.map(staff => (
                      <MenuItem key={staff.id} value={staff.id}>
                        {staff.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </SectionCard>

            {/* 일정 정보 */}
            <SectionCard>
              <SectionHeader variant="label">{t('일정 정보')}</SectionHeader>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                <FormLabel>{t('상담 날짜')}</FormLabel>
                <CommonDatePicker
                  value={formData.consultDate ? formData.consultDate.toDate() : null}
                  onChange={date => handleChange('consultDate', date ? dayjs(date) : null)}
                  disabled={isDetailMode}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormLabel>{t('상담 시간')}</FormLabel>
                <TimeRow>
                  <FormControl size="small" sx={{ minWidth: '70px', mr: '5px' }}>
                    <Select
                      value={formData.startHour}
                      onChange={e => handleChange('startHour', Number(e.target.value))}
                      disabled={isDetailMode}>
                      {HOUR_OPTIONS.map(h => (
                        <MenuItem key={h} value={h}>
                          {String(h).padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TimeColon>:</TimeColon>
                  <FormControl size="small" sx={{ minWidth: '70px', mr: '10px', ml: '5px' }}>
                    <Select
                      value={formData.startMinute}
                      onChange={e => handleChange('startMinute', Number(e.target.value))}
                      disabled={isDetailMode}>
                      {MINUTE_OPTIONS.map(m => (
                        <MenuItem key={m} value={m}>
                          {String(m).padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TimeTilde>~</TimeTilde>
                  <FormControl size="small" sx={{ minWidth: '70px', mr: '5px' }}>
                    <Select
                      value={formData.endHour}
                      onChange={e => handleChange('endHour', Number(e.target.value))}
                      disabled={isDetailMode}>
                      {HOUR_OPTIONS.map(h => (
                        <MenuItem key={h} value={h}>
                          {String(h).padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TimeColon>:</TimeColon>
                  <FormControl size="small" sx={{ minWidth: '70px', ml: '5px' }}>
                    <Select
                      value={formData.endMinute}
                      onChange={e => handleChange('endMinute', Number(e.target.value))}
                      disabled={isDetailMode}>
                      {MINUTE_OPTIONS.map(m => (
                        <MenuItem key={m} value={m}>
                          {String(m).padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TimeRow>
              </Box>
            </SectionCard>

            {/* 상담 정보 */}
            <SectionCard>
              <SectionHeader variant="label">{t('상담 정보')}</SectionHeader>

              <TwoColumnRow>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormLabel>{t('상담 수단')}</FormLabel>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      displayEmpty
                      value={formData.consultMethod}
                      onChange={e => handleChange('consultMethod', e.target.value)}
                      disabled={isDetailMode}>
                      <MenuItem value="">{t('선택 안함')}</MenuItem>
                      {CONSULT_METHOD_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormLabel>{t('상담 목적')}</FormLabel>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      displayEmpty
                      value={formData.consultPurpose}
                      onChange={e => handleChange('consultPurpose', e.target.value)}
                      disabled={isDetailMode}>
                      <MenuItem value="" disabled>
                        {t('선택')}
                      </MenuItem>
                      {CONSULT_PURPOSE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </TwoColumnRow>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: '10px' }}>
                <FormLabel>{t('상담 결과')}</FormLabel>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={formData.consultResult || 'SCHEDULED'}
                    onChange={e => handleChange('consultResult', e.target.value)}
                    disabled={isDetailMode}>
                    {CONSULT_RESULT_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mt: '10px' }}>
                <FormLabel>{t('방문경로')}</FormLabel>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    displayEmpty
                    value={formData.visitPath}
                    onChange={e => handleChange('visitPath', e.target.value)}
                    disabled={isDetailMode}>
                    <MenuItem value="">{t('선택 안함')}</MenuItem>
                    {VISIT_PATH_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {formData.visitPath === VISIT_PATH_OTHER && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: '10px' }}>
                  <FormLabel />
                  <TextField
                    size="small"
                    sx={inputSx}
                    placeholder={`방문경로 직접 입력 (${VISIT_PATH_ETC_MAX_LENGTH}자 이내)`}
                    value={formData.visitPathEtc}
                    onChange={e => handleChange('visitPathEtc', e.target.value)}
                    inputProps={{ maxLength: VISIT_PATH_ETC_MAX_LENGTH }}
                    disabled={isDetailMode}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: '10px' }}>
                <FormLabel alignTop>{t('운동목적')}</FormLabel>
                <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {exercisePurposeOptions.map(option => {
                    const selected = formData.exercisePurposes.includes(option.value);
                    return (
                      <Box
                        key={option.value}
                        onClick={() => !isDetailMode && togglePurpose(option.value)}
                        sx={{
                          height: '34px',
                          padding: '0 14px',
                          borderRadius: '17px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: '13px',
                          cursor: isDetailMode ? 'default' : 'pointer',
                          whiteSpace: 'nowrap',
                          backgroundColor: selected ? '#E6F2F3' : '#fff',
                          border: `1px solid ${selected ? '#0F7F85' : '#d9d9d9'}`,
                          color: selected ? '#0F7F85' : '#5f5f5f',
                          fontWeight: selected ? 600 : 400,
                        }}>
                        {t(option.label)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: '10px' }}>
                <FormLabel alignTop>{t('상담내용')}</FormLabel>
                <StyledTextarea
                  minRows={3}
                  style={{ flex: 1, height: '70px', fontSize: '13px', width: 'auto' }}
                  placeholder={t('상담 내용을 입력해 주세요')}
                  value={formData.content}
                  onChange={e => handleChange('content', e.target.value)}
                  disabled={isDetailMode}
                />
              </Box>
            </SectionCard>
          </ContentInner>
        </ContentArea>

        <FooterBar>
          <Box />
          <FooterRightGroup>
            {isDetailMode ? (
              <>
                <CommonModalButton variant="cancel" onClick={handleClose}>
                  {t('닫기')}
                </CommonModalButton>
                <CommonModalButton variant="submit" onClick={() => onEdit?.(editData)}>
                  {t('수정')}
                </CommonModalButton>
              </>
            ) : (
              <>
                <CommonModalButton variant="cancel" onClick={handleClose}>
                  {t('취소')}
                </CommonModalButton>
                <CommonModalButton variant="submit" onClick={handleSubmit}>
                  {isEditMode ? t('수정') : t('저장')}
                </CommonModalButton>
              </>
            )}
          </FooterRightGroup>
        </FooterBar>
      </ModalContainer>
    </Modal>
  );
};

export default ConsultationEdit;
